import { useContext, useEffect, useEffectEvent, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useLocation } from 'react-router'
import { Add, CloseOutlined, DeleteOutlined, Launch } from '@mui/icons-material'
import { Backdrop, Box, Button, Checkbox, Divider, Grid, IconButton, Skeleton, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import Search from '../../../../components/search'
import { AppContext } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import { getFolderBreadcrumbs } from '../../../../utils/helpers'
import useSharedStyles from '../../../../utils/shared.styles'
import PeopleContext from '../../context'
import usePeopleStyles from '../../index.styles'

import useStyles from './index.styles'
import RemoveModal from './remove-modal'

interface IProps {
  isEditing: boolean
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
}

const AccountsContent = (props: IProps) => {
  const { isEditing, setIsEditing } = props

  const classes = useStyles
  const peopleClasses = usePeopleStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()

  const { enqueueSnackbar } = useSnackbar()
  const location = useLocation()

  const { tree, host, tenantId, pageRefreshCounter } = useContext(AppContext)
  const { user, setUser } = useContext(PeopleContext)
  const auth = useAuth()

  const inputRef = useRef<HTMLInputElement>(undefined)

  const [loading, setLoading] = useState<boolean>(true)
  const [folders, setFolders] = useState<IFolderType[]>([])
  const [showLaunchButton, setShowLaunchButton] = useState<number>()
  const [foldersToRemove, setFoldersToRemove] = useState<number[]>([])
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false)
  const [removingFolders, setRemovingFolders] = useState<boolean>(false)
  const [showAccountSearch, setShowAccountSearch] = useState<boolean>(false)
  const [foldersToAdd, setFoldersToAdd] = useState<ISearchResultAccount[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const folderList = [...foldersToAdd, ...folders]

  const removeFolders = async () => {
    if (user) {
      setRemovingFolders(true)
      // Remove from foldersToAdd
      setFoldersToAdd((prev) => prev.filter((f) => !foldersToRemove.includes(f.id)))
      // Remove from user folders
      if (user.folders.filter((f) => foldersToRemove.includes(f)).length > 0) {
        const payload = {
          ...user,
          folders: user.folders.filter((f) => !foldersToRemove.includes(f)),
        }
        const res = await Api.put(`${host}/api/users/${user.id}`, payload)
        if (res.ok) {
          setUser(res.body)
          enqueueSnackbar('Accounts removed successfully')
          setFoldersToRemove([])
          setIsEditing(false)
        } else {
          enqueueSnackbar('Something went wrong, and accounts could not be removed', { variant: 'error' })
        }
      } else {
        enqueueSnackbar('Accounts removed successfully')
        setFoldersToRemove([])
      }

      setRemovingFolders(false)
    }
  }

  const addFolders = async () => {
    if (user) {
      setIsSaving(true)
      const payload = {
        ...user,
        folders: folderList.map((f) => f.id),
      }
      const res = await Api.put(`${host}/api/users/${user.id}`, payload)
      if (res.ok) {
        setUser(res.body)
        enqueueSnackbar('Accounts added successfully')
        setFoldersToAdd([])
        setIsEditing(false)
      } else {
        enqueueSnackbar('Something went wrong, and accounts could not be added', { variant: 'error' })
      }
      setIsSaving(false)
    }
  }

  const fetchData = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token || !user) return
    setLoading(true)
    const res = await Api.post(`${host}/api/folders/query?detail=true`, {
      filters: [{ fieldName: 'id', operator: 'in', value: user.folders }],
      first: 10000,
      type: 'and',
    })
    if (res.ok) {
      setFolders(res.body.results)
    } else {
      enqueueSnackbar('Something went wrong, and accounts did not load', { variant: 'error' })
    }
    setLoading(false)
  })
  useEffect(() => {
    fetchData()
  }, [user, host, pageRefreshCounter])

  useEffect(() => {
    if (location.hash) {
      const element = document?.querySelector(location.hash)
      if (element) {
        element.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [location])

  return (
    <>
      {loading && (
        <Box sx={classes.accountListRow} id="user-accounts">
          <Skeleton width="400px" />
          <Skeleton width="320px" />
        </Box>
      )}
      {!loading && (
        <>
          {isEditing && (
            <>
              <Grid container={true} sx={[sharedClasses.centerContentVertical, { justifyContent: 'space-between', padding: 1 }]}>
                <Grid>
                  <Grid container={true} sx={sharedClasses.centerContentVertical}>
                    <Checkbox
                      checked={foldersToRemove.length === folderList.length}
                      indeterminate={foldersToRemove.length > 0 && foldersToRemove.length < folderList.length}
                      onChange={(_e, checked) => (checked ? setFoldersToRemove(folderList.map((f) => f.id)) : setFoldersToRemove([]))}
                      slotProps={{
                        input: { 'aria-label': 'Select all' },
                      }}
                    />
                    <Typography variant="subtitle2">Assigned ({folderList.length})</Typography>
                  </Grid>
                </Grid>

                <Grid>
                  {user && foldersToRemove.length > 0 && (
                    <Button
                      color="error"
                      startIcon={<DeleteOutlined />}
                      size="small"
                      disabled={removingFolders}
                      onClick={() => (user.folders.filter((f) => foldersToRemove.includes(f)).length > 0 ? setShowRemoveModal(true) : removeFolders())}
                      sx={peopleClasses.rightSpaceButton}
                    >
                      Remove {foldersToRemove.length} Selected
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    size="small"
                    onClick={() => {
                      setShowAccountSearch(true)
                      setTimeout(() => inputRef.current?.focus(), 200)
                    }}
                  >
                    Add Account
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={classes.divider} />

              <Backdrop open={showAccountSearch} onClick={() => setShowAccountSearch(false)} sx={classes.backdrop}>
                <Box sx={classes.backdropSearchContainer} onClick={(e) => e.stopPropagation()}>
                  <Search
                    closeOverlay={() => setShowAccountSearch(false)}
                    allowedTypes={['accounts']}
                    onSelect={(item) => {
                      setShowAccountSearch(false)
                      setFoldersToAdd((prev) => {
                        if (folderList.find((f) => f.id === (item as ISearchResultAccount).id)) return prev
                        return [...prev, item as ISearchResultAccount]
                      })
                    }}
                  />
                </Box>
                <IconButton onClick={() => setShowAccountSearch(false)} aria-label="Close search" sx={classes.backdropCloseIconButton}>
                  <CloseOutlined sx={classes.backdropCloseIcon} />
                </IconButton>
              </Backdrop>
            </>
          )}

          <Box sx={{ ...classes.accountListContainer(theme), padding: isEditing ? '0 24px' : '0 34px' }} id="user-accounts">
            {folderList.map((folder) => (
              <Grid
                key={folder.id}
                container={true}
                onMouseEnter={() => setShowLaunchButton(folder.id)}
                onMouseLeave={() => setShowLaunchButton(undefined)}
                onFocus={() => setShowLaunchButton(folder.id)}
                tabIndex={0}
                aria-label={`Show launch button for ${folder?.name}`}
                sx={[
                  {
                    justifyContent: 'space-between',
                  },
                  classes.accountListRow,
                ]}
              >
                <Box>
                  <Grid container={true} sx={sharedClasses.centerContentVertical}>
                    {isEditing && (
                      <Checkbox
                        checked={foldersToRemove.includes(folder.id)}
                        onChange={(_e, checked) =>
                          checked ? setFoldersToRemove((prev) => [...prev, folder.id]) : setFoldersToRemove((prev) => prev.filter((f) => f !== folder.id))
                        }
                        slotProps={{
                          input: { 'aria-label': `Select ${folder?.name}` },
                        }}
                      />
                    )}
                    <Grid>
                      <Typography variant="body1" className="fs-exclude">
                        {folder.name}
                      </Typography>

                      {tree && (
                        <Typography variant="body2" color="secondary" className="fs-exclude">
                          {getFolderBreadcrumbs({ tree, folderId: folder.id })}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ minHeight: '54px' }}>
                  {showLaunchButton === folder.id && (
                    <IconButton
                      component="a"
                      href={`/tenant/${tenantId}/accounts/${folder.id}`}
                      target="_blank"
                      color="primary"
                      onBlur={() => setShowLaunchButton(undefined)}
                      aria-label={`Open ${folder?.name} in a new tab`}
                    >
                      <Launch />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Box>

          {isEditing && (
            <>
              <Divider sx={classes.divider} />
              <Box sx={peopleClasses.bottomButtonsContainer}>
                <Button variant="contained" disabled={isSaving || foldersToAdd.length === 0} onClick={addFolders} sx={peopleClasses.rightSpaceButton}>
                  Save
                </Button>

                <Button
                  onClick={() => {
                    setFoldersToAdd([])
                    setIsEditing(false)
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}

          <RemoveModal open={showRemoveModal} setOpen={setShowRemoveModal} onRemove={removeFolders} />
        </>
      )}
    </>
  )
}

export default AccountsContent
